import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Ticket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type'); // 'event_pass' or null (membership)
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [passGranted, setPassGranted] = useState(false);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  // Grant event pass once payment is confirmed
  useEffect(() => {
    if (status === 'success' && paymentType === 'event_pass' && token && !passGranted) {
      grantEventPass();
    }
  }, [status, paymentType, token, passGranted]);

  const grantEventPass = async () => {
    try {
      // Get the transaction to find event_id and pass_tier_id
      const txRes = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
      const txData = await txRes.json();
      const meta = txData.metadata || {};
      const eventId = meta.event_id;
      const passTierId = meta.pass_tier_id;
      if (!eventId || !passTierId) return;

      const res = await fetch(`${API_URL}/api/events/${eventId}/passes/${passTierId}/grant`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (res.ok) {
        setPassGranted(true);
        setPaymentInfo(prev => ({ ...prev, event_id: eventId, pass_name: meta.pass_name }));
      }
    } catch (err) {
      console.error('Error granting event pass:', err);
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/payments/status/${sid}`);
      const data = await res.json();
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(data);
        // Refresh user data so subscription_status updates (removes payment gate)
        if (refreshUser) refreshUser();
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  };

  const isEventPass = paymentType === 'event_pass';

  return (
    <div className="min-h-screen bg-rooam-black flex items-center justify-center px-4">
      <Card className="bg-rooam-gray-medium border-rooam-gray-light max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 text-rooam-blue mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
              <p className="text-gray-400">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              {isEventPass ? (
                <Ticket className="h-16 w-16 text-[#FF453A] mx-auto mb-4" />
              ) : (
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              )}
              <h2 className="text-2xl font-bold text-white mb-2">
                {isEventPass ? 'Event Pass Activated!' : 'Payment Successful!'}
              </h2>
              <p className="text-gray-400 mb-4">
                {isEventPass 
                  ? `Your ${paymentInfo?.pass_name || 'event pass'} is now active. Enjoy the games!`
                  : 'Thank you for your purchase.'
                }
              </p>
              {paymentInfo && (
                <div className="bg-rooam-black p-4 rounded-lg mb-6">
                  <p className="text-sm text-gray-400">Amount Paid</p>
                  <p className="text-2xl font-bold text-rooam-blue">
                    ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                  </p>
                </div>
              )}
              <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold w-full" asChild>
                <Link to={isEventPass && paymentInfo?.event_id ? `/event/${paymentInfo.event_id}` : '/browse'}>
                  {isEventPass ? 'Back to Event' : 'Start Watching'}
                </Link>
              </Button>
            </>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">
                {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
              </h2>
              <p className="text-gray-400 mb-6">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.'
                  : 'There was an issue processing your payment. Please contact support.'}
              </p>
              <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold w-full" asChild>
                <Link to="/dashboard">Return to Dashboard</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
